import type { CardId } from '@voll-intelligence/domain';

import { t } from '@web/i18n';
import { useCardCustomerProfile, useCardDetail } from '@web/ui/core/hooks';
import { ChannelChip, CustomerAvatar, Dash, Mono, Row, Section } from '@web/ui/core/molecules';

import { ContactRows } from '../components/contact_rows';

export interface IdentityViewProps {
  cardId: CardId;
}

/**
 * Identity tab. Mirrors the v2 mockup's `ClienteTab`: a customer head
 * (avatar + name + company) followed by a "Cliente" and a "Card" key-value
 * block. Resolves its own card detail.
 *
 * Card-scoped rows come from `CardDetail`; e-mail / telefone / programas de
 * milhas come from the traveler profile (`useCardCustomerProfile`, dossiê do
 * customer-api) and degrade to `—` when it is unavailable. The customer
 * metric rows (tickets este mês / CSAT / limite mensal) stay as `—` — no
 * data source is wired for them yet.
 */
export function IdentityView({ cardId }: Readonly<IdentityViewProps>): React.JSX.Element {
  const detailQuery = useCardDetail(cardId);
  const profileState = useCardCustomerProfile(cardId);

  if (detailQuery.isLoading) {
    return <p className="text-meta text-muted-foreground">{t('board.drawer.tasks.loading')}</p>;
  }
  if (detailQuery.isError || !detailQuery.data) {
    return <p className="text-meta text-tone-red-fg">{t('board.drawer.errorLoading')}</p>;
  }

  const detail = detailQuery.data;

  return (
    <div data-testid="drawer-identity-tab" className="flex flex-col gap-4">
      <header className="flex items-center gap-3">
        <CustomerAvatar name={detail.customer.name} vip={detail.customer.vip} size="lg" />
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-base font-medium text-foreground">
            {detail.customer.name}
          </span>
          <span className="truncate text-meta text-muted-foreground">{detail.company.name}</span>
        </div>
      </header>

      <Section title={t('board.drawer.identity.customer.title')}>
        <Row label={t('card.drawer.customer.idLabel')} value={<Mono>{detail.customer.id}</Mono>} />
        <Row
          label={t('card.drawer.customer.channelPreferred')}
          value={<ChannelChip channel={detail.originChannel} />}
        />
        <ContactRows state={profileState} />
        <Row label={t('card.drawer.customer.ticketsMonth')} value={<Dash />} />
        <Row label={t('card.drawer.customer.csat')} value={<Dash />} />
        <Row label={t('card.drawer.customer.monthlyLimit')} value={<Dash />} />
      </Section>

      <Section title={t('board.drawer.identity.card.title')}>
        <Row label={t('board.drawer.identity.card.ticket')} value={<Mono>#{detail.seq}</Mono>} />
        <Row
          label={t('board.drawer.identity.card.squad')}
          value={detail.squadId ? <Mono>{detail.squadId}</Mono> : <Dash />}
        />
        <Row
          label={t('board.drawer.identity.card.project')}
          value={detail.projectId ? <Mono>{detail.projectId}</Mono> : <Dash />}
        />
      </Section>
    </div>
  );
}
